import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getEvalReplayOptions } from '@/api/generated/@tanstack/react-query.gen';
import { Skeleton } from '@/components/ui/skeleton';
import { EvalShell, Panel } from '@/components/Evals/EvalShell';
import { StatusPill } from '@/components/Evals/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/evals/replays/$replayId')({
  component: ReplayPage,
});

function Verdict({ label, why }: { label: string; why: string }) {
  if (!label) return null;
  return (
    <p className={cn('mt-1 text-[11px]', label === 'bad' ? 'text-rose-600' : 'text-emerald-600')} title={why}>
      judge: {label}
      <span className="text-neutral-500"> · {why}</span>
    </p>
  );
}

function ReplayPage() {
  const { replayId } = Route.useParams();
  const query = useQuery({
    ...getEvalReplayOptions({ path: { replay_id: replayId } }),
    refetchInterval: (q) => (['queued', 'running'].includes(q.state.data?.replay.status ?? '') ? 4000 : false),
  });
  const data = query.data;
  const s = data?.replay.summary;
  return (
    <EvalShell
      title={data?.replay.name ?? 'Replay'}
      crumbs={[{ label: 'Replay lab', to: '/evals/replays' }]}
      subtitle={
        data
          ? `${data.replay.variant.model}${data.replay.variant.extra_instruction ? ` · added: “${data.replay.variant.extra_instruction}”` : ''}${data.replay.variant.instructions != null ? ' · base instructions replaced' : ''}`
          : undefined
      }
      actions={data && <StatusPill status={data.replay.status} />}
    >
      {!data ? (
        <Skeleton className="h-96 rounded-3xl" />
      ) : (
        <>
          {s && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { label: 'Good lines', value: `${s.original_good ?? 0} → ${s.new_good ?? 0}`, sub: `of ${s.lines ?? 0}` },
                { label: 'Fixed', value: `+${s.fixed ?? 0}`, sub: 'bad → good', tone: 'text-emerald-600' },
                { label: 'Broke', value: `−${s.broke ?? 0}`, sub: 'good → bad', tone: 'text-rose-600' },
                { label: 'Errors', value: String(s.errors ?? 0), sub: 'lines not replayed' },
              ].map((c) => (
                <Panel key={c.label}>
                  <p className="text-[11px] uppercase tracking-wide text-neutral-400">{c.label}</p>
                  <p className={cn('text-2xl font-semibold tracking-tight text-neutral-900', c.tone)}>{c.value}</p>
                  <p className="text-[11px] text-neutral-400">{c.sub}</p>
                </Panel>
              ))}
            </div>
          )}
          <Panel title="Lines">
            {data.replay.status !== 'done' && !data.results.length ? (
              <p className="text-sm text-neutral-400">
                {data.replay.status === 'failed' ? 'The replay failed.' : 'The runner is replaying the lines…'}
              </p>
            ) : (
              <div className="divide-y divide-neutral-100">
                {data.results.map((r) => {
                  const changed = r.original_label && r.new_label && r.original_label !== r.new_label;
                  return (
                    <div key={`${r.run_id}-${r.target_id}`} className="grid gap-3 py-3 md:grid-cols-2">
                      <div>
                        <p className="mb-1 text-[10px] uppercase tracking-wide text-neutral-400">
                          Original{r.step ? ` · ${r.step}` : ''}
                          {r.human_label && (
                            <span className={r.human_label === 'bad' ? 'text-rose-500' : 'text-emerald-500'}>
                              {' '}
                              · you: {r.human_label}
                            </span>
                          )}
                        </p>
                        <p className="text-[13px] text-neutral-700">{r.original_text}</p>
                        <Verdict label={r.original_label ?? ''} why={r.original_explanation ?? ''} />
                        <Link
                          to="/reviews/$roomId"
                          params={{ roomId: r.room_id }}
                          className="mt-1 inline-block text-[11px] font-medium text-neutral-500 hover:underline"
                        >
                          Open session
                        </Link>
                      </div>
                      <div className={cn('rounded-2xl px-3 py-2', changed ? (r.new_label === 'good' ? 'bg-emerald-50' : 'bg-rose-50') : 'bg-neutral-50')}>
                        <p className="mb-1 text-[10px] uppercase tracking-wide text-neutral-400">Replayed</p>
                        {r.error ? (
                          <p className="text-[12px] text-rose-600">{r.error}</p>
                        ) : (
                          <>
                            <p className="text-[13px] text-neutral-900">{r.new_text}</p>
                            <Verdict label={r.new_label ?? ''} why={r.new_explanation ?? ''} />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </>
      )}
    </EvalShell>
  );
}
